/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Modal,
  TreeSelect,
} from 'antd';
import { formatReceived } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import { getAreas } from 'api/catalogos/index';
import {
  getUsuariosPermisosAreas,
  postUsuariosPermisosAreas,
  deleteUsuariosPermisosAreas,
  patchUsuariosPermisosAreas,
  getUsuarioPermisoArea,
} from 'api/nominas/catalogos/usuariosLimitacionesAreas';
import { checkToken } from 'api/utils';
import { ESTATUS_AUTORIZADO } from 'utils/constants';

export const usuariosPermisosAreasPermission = {
  permissionModel: 'usuariolimitaciondearea',
  catalogsModels: [],
  tabsModels: [],
};

const UsuariosPermisosAreas = ({ permission, selected, disableEditar }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [areas, setAreas] = useState([]);
  const [flatAreas, setFlatAreas] = useState([]);
  const [data, setData] = useState([]);

  const formInstances = [form];

  const fetchData = async () => {
    setLoading(true);
    const _data = await getUsuariosPermisosAreas({ usuario_limitacion_de_informacion: selected });
    setData(formatReceived(_data));
    setLoading(false);
  };

  const normalizeDataAreas = (_areasFetchArray) => {
    const mainMap = new Map();
    _areasFetchArray.forEach((item) => {
      mainMap.set(item.id, { ...item, children: [] });
    });
    const result = [];
    _areasFetchArray.forEach((item) => {
      if (item.directorio_area_padre) {
        const parent = mainMap.get(item.directorio_area_padre);
        if (parent) {
          parent.children.push(mainMap.get(item.id));
        }
      } else {
        result.push(mainMap.get(item.id));
      }
    });
    const sortByClave = (nodes) => {
      nodes.sort((a, b) => a.clave.localeCompare(b.clave));
      nodes.forEach((node) => {
        if (node.children && node.children.length > 0) {
          sortByClave(node.children);
        }
      });
    };
    sortByClave(result);
    return result;
  };

  const formatTreeSelectData = (nodes) => nodes.map((node) => ({
    title: `${node.clave} - ${node.nombre?.trim() || ''}`,
    value: node.id,
    children: node.children ? formatTreeSelectData(node.children) : [],
  }));

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _areas,
        _data,
      ] = await Promise.all([
        getAreas({ estados_globales: ESTATUS_AUTORIZADO }),
        getUsuariosPermisosAreas({ usuario_limitacion_de_informacion: selected }),
      ]);

      if (mounted) {
        setFlatAreas(_areas); // Mantenemos la lista original para la tabla
        const formattedURS = formatTreeSelectData(normalizeDataAreas(_areas));
        setAreas(formattedURS);
        setData(formatReceived(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setSelectedRowKeys([]);
    setVisible(false);
    form.resetFields();
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _permiso = await getUsuarioPermisoArea(match.id);

    // Asignamos directamente el area_content_id que ya trae el permiso
    _permiso.unidad_responsable = _permiso.area_content_id;

    setTimeout(() => {
      if (_permiso) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceived(_permiso));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();

      const values = {
        ...form.getFieldsValue(),
        // Pasamos directamente el valor del TreeSelect al area_content_id
        area_content_id: form.getFieldValue('unidad_responsable'),
      };
      const [key] = selectedRowKeys;

      const saved = await (key
        // eslint-disable-next-line max-len
        ? patchUsuariosPermisosAreas(key, { ...values, usuario_limitacion_de_informacion: selected })
        : postUsuariosPermisosAreas({ ...values, usuario_limitacion_de_informacion: selected })
      );

      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteUsuariosPermisosAreas(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };
  const columns = [
    {
      titleText: 'Área',
      dataIndex: 'directorio_de_area',
      key: 'directorio_de_area',
      render: (val) => {
        const area = flatAreas.find((a) => a.id === val);
        return area ? area.clave_y_nombre : '';
      },
      width: 200,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          permission={permission}
          allowExportPDF={false}
          allowAdd={!disableEditar}
          allowDelete={!disableEditar}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Permisos Áreas`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              allowSave={!disableEditar}
            />
            )}
          onCancel={onCancel}
          forceRender
          width="45%"
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
          >
            <Row gutter={10}>
              <Col xs={24} sm={24} md={24}>
                <Form.Item
                  name="directorio_de_area"
                  label="Área"
                  rules={rules.required}
                  hasFeedback
                >
                  <TreeSelect
                    showSearch
                    treeNodeFilterProp="title"
                    style={{ width: '100%' }}
                    dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                    treeData={areas}
                    disabled={disableEditar}
                    placeholder="Seleccione un área"
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Row>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`el Área ${flatAreas.find((a) => a.id === data.find((item) => item.id === selectedRowKeys[0])?.area_content_id)?.clave_y_nombre}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default UsuariosPermisosAreas;
